import { Search } from 'lucide-react';

import { EvidenceDistribution } from './EvidenceDistribution';
import { EvidenceItemList } from './EvidenceItemList';
import { countFor, EVIDENCE_STATUSES, STATUS_LABEL } from './evidence';

import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type {
  ReadinessEvidenceCounts,
  ReadinessEvidenceItem,
  ReadinessEvidenceStatus,
} from '@/lib/api/readiness';

/** `all` is the absence of a status filter, not a status the API knows. */
export type EvidenceStatusFilter = ReadinessEvidenceStatus | 'all';

export interface ReadinessEvidenceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The component's own label, e.g. "Curriculum progress". */
  title: string;
  /** The arithmetic in words, over the whole set. */
  summary?: string;
  /** Always the UNFILTERED distribution — it is what the filters are counted from. */
  counts?: ReadinessEvidenceCounts | null;
  /** Every page loaded so far, concatenated, in the API's order. */
  items: ReadinessEvidenceItem[];
  /** Matches after filtering — what "Showing X of Y" and paging are measured against. */
  total: number;
  /** First page in flight (a filter change counts as a first page). */
  loading?: boolean;
  /** A subsequent page in flight. */
  loadingMore?: boolean;
  error?: boolean;
  onRetry?: () => void;
  status: EvidenceStatusFilter;
  onStatusChange: (status: EvidenceStatusFilter) => void;
  query: string;
  onQueryChange: (query: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  onNavigate?: (href: string) => void;
}

/**
 * The full drill-down for one readiness component.
 *
 * The card can only ever show the worst handful — a 327-topic curriculum
 * rendered inline is a page thousands of rows long and a question nobody can
 * answer. Real analysis happens here instead: search, filter by status, page
 * through, follow a record to the thing it is about.
 *
 * Presentational on purpose. It fetches nothing and knows no routes; the
 * container above it owns the query, the debounce and the paging, which is
 * what lets this be rendered in a test with a fixed list.
 */
export function ReadinessEvidenceDrawer({
  open,
  onOpenChange,
  title,
  summary,
  counts,
  items,
  total,
  loading = false,
  loadingMore = false,
  error = false,
  onRetry,
  status,
  onStatusChange,
  query,
  onQueryChange,
  hasMore,
  onLoadMore,
  linkFor,
  onNavigate,
}: ReadinessEvidenceDrawerProps) {
  const filtered = status !== 'all' || query.trim() !== '';
  // Zero-count statuses are dropped rather than disabled: a tab that can only
  // ever return nothing is a control that wastes a tap and a line of width.
  const presentStatuses = EVIDENCE_STATUSES.filter((s) => countFor(counts ?? undefined, s) > 0);
  // A filter that filters nothing is worse than no filter. When every record
  // shares one status the tabs read "All 92 | Costing score 92" — two controls
  // offering the same 92 rows, which makes the reader hunt for a difference
  // that does not exist.
  const statusTabs = presentStatuses.length > 1 ? presentStatuses : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        // Full-bleed below `sm` so this is usable at 390px, where a 75%-width
        // drawer would leave the value column unreadable.
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
      >
        <SheetHeader className="space-y-2 border-b px-4 py-4 sm:px-5">
          <SheetTitle className="pr-8 text-base">{title}</SheetTitle>
          <SheetDescription className="text-xs leading-relaxed">
            {summary || 'Every record behind this input.'}
          </SheetDescription>
          <EvidenceDistribution counts={counts} loading={loading} size="md" className="pt-0.5" />
        </SheetHeader>

        <div className="space-y-2.5 border-b px-4 py-3 sm:px-5">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search these records"
              aria-label={`Search ${title} records`}
              className="h-9 pl-8 text-sm"
            />
          </div>

          {statusTabs.length > 0 && (
            <Tabs value={status} onValueChange={(v) => onStatusChange(v as EvidenceStatusFilter)}>
              <TabsList
                aria-label="Filter by status"
                className="h-auto w-full flex-wrap justify-start gap-1 p-1"
              >
                <TabsTrigger value="all" className="px-2 py-1 text-xs">
                  All
                  <CountPill n={counts?.total ?? 0} />
                </TabsTrigger>
                {statusTabs.map((s) => (
                  <TabsTrigger key={s} value={s} className="px-2 py-1 text-xs">
                    {STATUS_LABEL[s]}
                    <CountPill n={countFor(counts ?? undefined, s)} />
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5">
          <Body
            error={error}
            onRetry={onRetry}
            loading={loading}
            items={items}
            filtered={filtered}
            linkFor={linkFor}
            onNavigate={onNavigate}
          />
        </div>

        {!error && !loading && items.length > 0 && (
          <div className="flex items-center justify-between gap-3 border-t px-4 py-3 sm:px-5">
            <p className="text-[11px] text-muted-foreground" aria-live="polite">
              Showing <span className="font-semibold tabular-nums text-foreground">{items.length}</span> of{' '}
              <span className="font-semibold tabular-nums text-foreground">{total}</span>
            </p>
            {hasMore && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onLoadMore}
                disabled={loadingMore}
                aria-busy={loadingMore}
                className="h-8 text-xs"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CountPill({ n }: { n: number }) {
  return (
    <span className="ml-1.5 rounded-full bg-foreground/10 px-1.5 text-[10px] font-semibold tabular-nums">
      {n}
    </span>
  );
}

function Body({
  error,
  onRetry,
  loading,
  items,
  filtered,
  linkFor,
  onNavigate,
}: {
  error: boolean;
  onRetry?: () => void;
  loading: boolean;
  items: ReadinessEvidenceItem[];
  filtered: boolean;
  linkFor?: (item: ReadinessEvidenceItem) => string | undefined;
  onNavigate?: (href: string) => void;
}) {
  if (error) {
    return (
      <div className="py-8 text-center">
        <p className="text-xs text-muted-foreground">These records couldn&apos;t be loaded.</p>
        {onRetry && (
          <Button type="button" variant="outline" size="sm" onClick={onRetry} className="mt-3 h-8 text-xs">
            Try again
          </Button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div aria-busy="true" className="space-y-2">
        <p className="sr-only">Loading these records…</p>
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className={cn('h-6 animate-pulse rounded bg-muted', i % 3 === 2 && 'w-2/3')} />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-xs text-muted-foreground">
        {filtered
          ? 'No records match.'
          : 'Nothing has been recorded for this component yet.'}
      </p>
    );
  }

  return <EvidenceItemList items={items} linkFor={linkFor} onNavigate={onNavigate} groupByDetail />;
}
